import type { OrderItem, FulfillmentMode } from '../types';
import { itemGrossAmount, recalcItem } from './orderItemCalc';
import { applyTierDiscount } from './suggestPosB2BPrices';

/**
 * TCR-2425 — các phép "áp một giá trị của MỘT cột vào MỘT dòng" được rút ra
 * khỏi `OrderItemRow` (sửa tay từng ô) để dùng CHUNG với tính năng "sao chép
 * dữ liệu cho các dòng bên dưới" (`copyLineValues`).
 *
 * Vì sao phải rút chung thay vì viết lại công thức: sao chép là "làm hộ thu
 * ngân thao tác tay trên N dòng" — nếu hai bên lệch nhau dù chỉ một đồng thì
 * cùng một thao tác, nhập tay ra một số còn sao chép ra số khác, và không có
 * cách nào phát hiện từ màn hình. Test của tính năng copy (đối chiếu từng
 * trường hợp với handler) chỉ có giá trị khi cả hai gọi CÙNG một hàm.
 *
 * File này KHÔNG chứa state/UI, chỉ hàm thuần: nhận dòng hiện tại + giá trị mới
 * ⇒ trả về dòng đã tính lại tiền và cờ override.
 */

/**
 * Áp Số lượng mới vào một dòng — mirror `handleQtyBlur` của `OrderItemRow`.
 *
 * Nhánh CK dạng TIỀN giữ nguyên số tiền CK MỖI ĐƠN VỊ (chứ không nhân lại %):
 * `discountPercent` chỉ là số quy đổi, còn tiền CK thật là `discountAmount`.
 * Tính lại từ % (đã làm tròn 2 chữ số) sẽ lệch vài đồng so với chính sách ⇒
 * báo "Sửa CK" giả dù người dùng không đụng tới CK (xem TCR-2129).
 */
export function applyLineQuantity(
  item: OrderItem,
  quantity: number,
  fulfillmentMode: FulfillmentMode,
): OrderItem {
  const isQuantityMode = item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
  if (item.discountType === 'amount' && item.quantity > 0) {
    const grossAmount = itemGrossAmount(item.price, quantity, isQuantityMode);
    const discountAmount = Math.min(
      Math.round((item.discountAmount / item.quantity) * quantity),
      grossAmount,
    );
    const totalAmount = grossAmount - discountAmount;
    const discountPercent =
      item.price > 0 && quantity > 0
        ? Math.round((discountAmount / quantity / item.price) * 100 * 100) / 100
        : item.discountPercent;
    return { ...item, quantity, discountAmount, discountPercent, totalAmount };
  }
  return recalcItem({ ...item, quantity }, fulfillmentMode);
}

/**
 * Áp Đơn giá mới vào một dòng — mirror `handlePriceBlur`.
 *
 * `hasPriceOverride` là cờ "thu ngân tự sửa giá" (dùng để bộ lọc gộp combo loại
 * dòng ra và để payload gửi lý do sửa giá). Mốc so sánh KHÁC nhau theo kênh:
 * - B2B: mốc là giá GỢI Ý của cấp giá (`suggestedPrice ?? listPrice`);
 * - bán lẻ: mốc là giá lẻ gốc của dòng (`retailPrice ?? listPrice`).
 * Gõ đúng mốc ⇒ không phải sửa tay (`false`). Không có mốc ⇒ coi như có sửa.
 */
export function applyLinePrice(
  item: OrderItem,
  price: number,
  fulfillmentMode: FulfillmentMode,
  isB2B: boolean,
): OrderItem {
  if (isB2B) {
    const suggest = item.suggestedPrice ?? item.listPrice;
    // TCR-2614: dòng tách luôn là giá tự đặt, dù gõ trùng giá gợi ý.
    const hasPriceOverride =
      item.splitLine === true || (suggest == null ? price > 0 : Math.abs(price - suggest) >= 0.01);
    // TCR-2126 — giữ CK của dòng (CK cấp giá đổ từ bảng giá hoặc NVKD tự sửa),
    // tính lại trên giá mới.
    const repriced = applyTierDiscount({ ...item, price }, fulfillmentMode);
    return { ...repriced, price, hasPriceOverride };
  }
  const { discountAmount, totalAmount } = recalcItem({ ...item, price }, fulfillmentMode);
  const reference = item.retailPrice ?? item.listPrice;
  const hasPriceOverride =
    item.splitLine === true || reference == null || Math.abs(price - reference) >= 0.01;
  return { ...item, price, discountAmount, totalAmount, hasPriceOverride };
}

/**
 * Áp Chiết khấu dạng % vào một dòng — mirror `handleCkPctBlur` (kẹp 0..100).
 *
 * KHÔNG tự đổi `discountType`: handler gốc cũng chỉ đổi % vì ô % chỉ sửa được
 * khi cột "Loại" đang là `percent`. Người gọi (copy) tự set loại cho đích.
 */
export function applyLineDiscountPercent(
  item: OrderItem,
  discountPercent: number,
  fulfillmentMode: FulfillmentMode,
): OrderItem {
  const percent = Math.min(100, Math.max(0, discountPercent));
  return recalcItem({ ...item, discountPercent: percent }, fulfillmentMode);
}

/**
 * Áp Chiết khấu dạng TIỀN theo đơn vị vào một dòng — mirror `handleCkAmtBlur`
 * (`applyTierDiscount` nhánh VND dùng cùng công thức).
 *
 * `perUnitAmount` là số tiền CK của MỘT đơn vị (KHÔNG phải tổng tiền CK của cả
 * dòng). Quy ước này là điểm dễ sai nhất của tính năng sao chép: dòng nguồn
 * 4 × 100.000 có CK 40.000 nghĩa là 10.000/đv, dòng đích 3 đơn vị phải nhận
 * 30.000 — nhân thẳng 40.000 sang là thu sai tiền của khách.
 *
 * Kẹp 2 lớp: mỗi đơn vị không vượt đơn giá, và tổng CK không vượt thành tiền
 * gộp (cùng chuẩn MISA với `handleCkAmtBlur`: tiền dòng luôn là số nguyên ≥ 0).
 */
export function applyLineDiscountAmountPerUnit(
  item: OrderItem,
  perUnitAmount: number,
  fulfillmentMode: FulfillmentMode,
): OrderItem {
  const isQuantityMode = item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
  const grossAmount = itemGrossAmount(item.price, item.quantity, isQuantityMode);
  const perUnit = Math.min(Math.max(perUnitAmount, 0), Math.max(item.price, 0));
  const discountAmount = Math.min(
    Math.round(isQuantityMode ? perUnit * item.quantity : perUnit),
    grossAmount,
  );
  const discountPercent =
    item.price > 0 ? Math.round((perUnit / item.price) * 100 * 100) / 100 : 0;
  return {
    ...item,
    discountType: 'amount',
    discountPercent,
    discountAmount,
    totalAmount: grossAmount - discountAmount,
  };
}
